/**
 * WorkshopsCalendarDayCell — one day in the month/week grid: a date header
 * (with the Today badge), the day's workshop chips, and a "+N more" toggle when
 * a Month day is too dense. Owns its own expand state.
 */
import { useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';

import { WorkshopsCalendarDayCellChips } from './WorkshopsCalendarDayCellChips';
import { WorkshopsCalendarDayCellHeader } from './WorkshopsCalendarDayCellHeader';
import { WorkshopsCalendarDayCellMoreToggle } from './WorkshopsCalendarDayCellMoreToggle';
import type { DayClickHandler, WorkshopChip, WorkshopClickHandler } from './workshopsCalendarTypes';

// Density thresholds (Month only — Week always shows full chips):
//   ≤ FULL_LIMIT      → full chips
//   ≤ COMPACT_VISIBLE → all events as compact rows
//   else              → first COMPACT_VISIBLE compact rows + "+N more" toggle
const FULL_LIMIT = 2;
const COMPACT_VISIBLE = 4;

export interface WorkshopsCalendarDayCellProps {
  date: Date;
  dayWorkshops: WorkshopChip[];
  view: 'Month' | 'Week';
  isToday: boolean;
  isPast: boolean;
  outOfMonth: boolean;
  isFirstColumn: boolean;
  onDayClick?: DayClickHandler;
  onWorkshopClick?: WorkshopClickHandler;
}

interface CellFlags {
  isToday: boolean;
  outOfMonth: boolean;
  isPast: boolean;
  isFirstColumn: boolean;
  clickable: boolean;
}

function cellStyle({
  isToday,
  outOfMonth,
  isPast,
  isFirstColumn,
  clickable,
}: CellFlags): CSSProperties {
  return {
    padding: 5,
    minWidth: 0,
    cursor: clickable ? 'pointer' : 'default',
    borderLeft: isFirstColumn ? 'none' : '1px solid var(--nb-border)',
    background: isToday
      ? 'var(--nb-primary-soft)'
      : outOfMonth
        ? 'var(--nb-surface)'
        : 'transparent',
    opacity: outOfMonth ? 0.5 : isPast ? 0.82 : 1,
  };
}

export function WorkshopsCalendarDayCell({
  date,
  dayWorkshops,
  view,
  isToday,
  isPast,
  outOfMonth,
  isFirstColumn,
  onDayClick,
  onWorkshopClick,
}: WorkshopsCalendarDayCellProps): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const compact = view === 'Month' && dayWorkshops.length > FULL_LIMIT;
  const visible = compact && !expanded ? dayWorkshops.slice(0, COMPACT_VISIBLE) : dayWorkshops;
  const hiddenCount = dayWorkshops.length - COMPACT_VISIBLE;

  return (
    <div
      onClick={onDayClick ? () => onDayClick(date, dayWorkshops) : undefined}
      style={cellStyle({
        isToday,
        outOfMonth,
        isPast,
        isFirstColumn,
        clickable: Boolean(onDayClick),
      })}
    >
      <WorkshopsCalendarDayCellHeader
        date={date}
        isToday={isToday}
        outOfMonth={outOfMonth}
        view={view}
      />
      <WorkshopsCalendarDayCellChips
        workshops={visible}
        view={view}
        compact={compact}
        onWorkshopClick={onWorkshopClick}
      />
      {compact && dayWorkshops.length > COMPACT_VISIBLE && (
        <WorkshopsCalendarDayCellMoreToggle
          expanded={expanded}
          hiddenCount={hiddenCount}
          onToggle={() => setExpanded((v) => !v)}
        />
      )}
    </div>
  );
}
